import { I18nNamespace } from 'constants/i18n.const';
import { TIME_CONFIRM_BEFORE_NEXT } from 'constants/photo.const';
import { useShootingContext } from 'containers/selfe-booth/shooting/ShootingContext';
import { useSoundContext } from 'context/SoundContext';
import { emitSeqLog } from 'functions/seq-logging.func';
import { sortAscListOfNum } from 'helpers/array.helper';
import { isEqualVal } from 'helpers/string.helper';
import { useModal } from 'hooks/useModal';
import { useNewPhotoLifeCycleStep } from 'hooks/useNewPhotoLifeCycleStep';
import { useTranslation } from 'hooks/useTranslation';
import { filter, forEach, includes } from 'lodash';
import { CameraModel } from 'models/photo/camera.model';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useBackStopCameraLiveViewApiActionMutation,
  useGenerateVideoApiActionMutation,
} from 'store/api/camera.slice-api';
import { setSelfeAppStateAction } from 'store/features/app/selfeAppSlice';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';

export const useShootingActions = () => {
  const { t } = useTranslation([
    I18nNamespace.COMMON,
    I18nNamespace.PAGE,
    I18nNamespace.WARNING,
  ]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    isRetakePhoto, // trong giai đoạn chụp lại
    isShootingOfRetakingPhoto, // trong giai đoạn chụp lại, và đang chụp ảnh
  } = useAppSelector((state) => state.selfeApp);
  const {
    availableListCameras,
    isCountDownMode,
    setListIndexOfRetake,
    setSelectImageIndex,
    // setIsViewingResultPhoto,
    selectImageIndex,
    setIsDelayShooting,
  } = useShootingContext();
  const { openConfirmModal, closeConfirmModal } = useModal();
  const { getNextPath, getPrevPath } = useNewPhotoLifeCycleStep();
  const [generateVideoAction] = useGenerateVideoApiActionMutation();
  const [backStopLiveViewAction] = useBackStopCameraLiveViewApiActionMutation();
  const { audio } = useSoundContext();

  const handleRedirectPrev = () => {
    forEach(availableListCameras, (camera) => {
      if (camera?.ID) backStopLiveViewAction({ id: camera?.ID });
    });
    navigate(getPrevPath() || '');
  };

  const handleRedirectNext = (isShowConfirmBefore = false) => {
    emitSeqLog?.({ messageTemplate: `[Shooting] Shooting Finish!` });
    if (isShowConfirmBefore) {
      audio?.playRedundantDeposit?.();
      handleConfirmBeforeNext();
    } else {
      if (isCountDownMode) generateVideoAction();
      navigate(getNextPath() || '');
    }
  };

  const handleConfirmBeforeNext = () => {
    openConfirmModal({
      content: `${t('warning:cannotRetakeTitle')}`,
      PrevButtonProps: { children: t('common:no') },
      NextButtonProps: { children: t('common:yes') },
      duration: TIME_CONFIRM_BEFORE_NEXT,
      isUseTimer: true,
      onTimeUp: closeConfirmModal,
      onOk: () => {
        closeConfirmModal?.();
        handleRedirectNext();
      },
      onClose: closeConfirmModal,
    });
  };

  const handleSelectResultOfPhoto = (index: number) => () => {
    if (!isRetakePhoto || !isShootingOfRetakingPhoto) {
      setSelectImageIndex(index);
      // setIsViewingResultPhoto(true);
    }
  };

  // Click chọn/bỏ chụp lại ảnh
  const handleToggleRetakePhoto = useCallback(() => {
    setListIndexOfRetake((l: any) =>
      sortAscListOfNum(
        includes(l, selectImageIndex)
          ? filter(l, (i) => !isEqualVal(selectImageIndex, i))
          : [...l, selectImageIndex],
      ),
    );
  }, [selectImageIndex, setListIndexOfRetake]);

  // Click chọn camera
  const handleSwitchCamera = (id: CameraModel['ID']) => () => {
    dispatch(setSelfeAppStateAction({ selectedCameraId: id }));
  };

  // Confirm tiếp tục khi ở trong khoảng đệm giữa các phiên chụp
  const handleForceNextDuringDelayShooting = () => {
    setIsDelayShooting(false);
  };

  return {
    handleRedirectPrev,
    handleSwitchCamera,
    handleConfirmBeforeNext,
    handleSelectResultOfPhoto,
    handleToggleRetakePhoto,
    handleForceNextDuringDelayShooting,
    handleRedirectNext,
  };
};
